import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import {
  AppBar,
  Toolbar,
  IconButton,
  Avatar,
  Box,
  Typography,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControlLabel,
  Checkbox,
  DialogContentText,
  Snackbar,
  Alert,
  Tooltip,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import LogoutIcon from "@mui/icons-material/Logout";
import MenuIcon from "@mui/icons-material/Menu";
import AdminPanelSettingsIcon from "@mui/icons-material/AdminPanelSettings";
import MessageIcon from "@mui/icons-material/Message";
import ForumIcon from "@mui/icons-material/Forum";
import AssessmentIcon from "@mui/icons-material/Assessment";
import GroupWorkIcon from "@mui/icons-material/GroupWork";
import EventIcon from "@mui/icons-material/Event";
import SecurityIcon from "@mui/icons-material/Security";
import CloseIcon from "@mui/icons-material/Close";
import AppsIcon from "@mui/icons-material/Apps";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import CryptoJS from "crypto-js";
import { jwtDecode } from "jwt-decode";
import { API_ENDPOINTS } from "config/api";
import ProtectedComponent from "components/ProtectedComponent";

const AES_KEY = "7D3B8E69F1A84B7EA8A3F6792B44C926";
const AES_IV = "A1B2C3D4E5F60718";

const decryptText = (encryptedText) => {
  const key = CryptoJS.enc.Utf8.parse(AES_KEY);
  const iv = CryptoJS.enc.Utf8.parse(AES_IV);
  const decrypted = CryptoJS.AES.decrypt(encryptedText, key, {
    iv,
    mode: CryptoJS.mode.CBC,
    padding: CryptoJS.pad.Pkcs7,
  });
  return decrypted.toString(CryptoJS.enc.Utf8);
};

const encryptText = (plainText) => {
  const key = CryptoJS.enc.Utf8.parse(AES_KEY);
  const iv = CryptoJS.enc.Utf8.parse(AES_IV);
  return CryptoJS.AES.encrypt(plainText, key, {
    iv,
    mode: CryptoJS.mode.CBC,
    padding: CryptoJS.pad.Pkcs7,
  }).toString();
};

const AdminNavBar = ({
  onOpenChat,
  onOpenGroupChat,
  onOpenChapterReport,
  onOpenEventReport,
  onOpenScreenRights,
  onOpenAdminMenu,
}) => {
  const theme = useTheme();
  const isMobileNav = useMediaQuery(theme.breakpoints.down("md"));

  const [adminName, setAdminName] = useState("");
  const [logoutDialogOpen, setLogoutDialogOpen] = useState(false);
  const [reportMenuAnchor, setReportMenuAnchor] = useState(null);
  const [mobileMenuAnchor, setMobileMenuAnchor] = useState(null);
  const [isERPAdmin, setIsERPAdmin] = useState(false);
  const [countdown, setCountdown] = useState(null);
  const [timeLeft, setTimeLeft] = useState("--:--:--");
  const [showSessionDialog, setShowSessionDialog] = useState(false);
  const [passwordInput, setPasswordInput] = useState("");
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [sessionError, setSessionError] = useState("");
  const [logoutChecked, setLogoutChecked] = useState(false);
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });

  useEffect(() => {
    let session = localStorage.getItem("adminLogsessiondetails") || localStorage.getItem("adminsessiondetails");
    if (session) {
      try {
        const decrypted = decryptText(session);
        const parsed = JSON.parse(decrypted);
        if (parsed.StaffCode || parsed.staffCode) setAdminName(parsed.StaffCode || parsed.staffCode);
        else if (parsed.Usn || parsed.usn) setAdminName(parsed.Usn || parsed.usn);
        setIsERPAdmin(parsed.IsERPAdmin || parsed.isERPAdmin || false);

        const token = parsed.Token || parsed.token || parsed.TOKEN || parsed.accessToken || parsed.access_token;
        if (token) {
          const decoded = jwtDecode(token);
          const exp = decoded?.exp;
          if (exp) {
            const now = Math.floor(Date.now() / 1000);
            setCountdown(Math.max(exp - now, 0));
          }
        }
      } catch {
        setAdminName("");
        setIsERPAdmin(false);
        setCountdown(null);
        setTimeLeft("--:--:--");
      }
    }
  }, []);

  useEffect(() => {
    if (countdown === null) return;

    const formatTime = (seconds) => {
      const h = String(Math.floor(seconds / 3600)).padStart(2, "0");
      const m = String(Math.floor((seconds % 3600) / 60)).padStart(2, "0");
      const s = String(seconds % 60).padStart(2, "0");
      return `${h}:${m}:${s}`;
    };

    setTimeLeft(formatTime(countdown));

    const interval = setInterval(() => {
      setCountdown((prev) => {
        if (prev === null || prev <= 1) {
          clearInterval(interval);
          setShowSessionDialog(true);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [countdown]);

  const handleLogout = () => {
    localStorage.removeItem("adminLogsessiondetails");
    localStorage.removeItem("adminsessiondetails");
    window.location.href = "/BasicAuth";
  };

  const handleReportMenuOpen = (event) => {
    setReportMenuAnchor(event.currentTarget);
  };

  const handleReportMenuClose = () => {
    setReportMenuAnchor(null);
  };

  const closeMobileMenu = () => setMobileMenuAnchor(null);

  const handleSessionRefresh = async (e) => {
    if (e) e.preventDefault();
    setIsRefreshing(true);
    setSessionError("");

    try {
      const session = localStorage.getItem("adminLogsessiondetails") || localStorage.getItem("adminsessiondetails");
      if (!session) throw new Error("No admin session found");

      const decrypted = decryptText(session);
      const parsed = JSON.parse(decrypted);
      const staffCode = parsed.StaffCode || parsed.staffCode || parsed.Usn || parsed.usn;
      if (!staffCode) throw new Error("Staff code missing");

      const response = await fetch(API_ENDPOINTS.AUTH_ADMIN.STAFF_LOGIN, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          staffCode: encryptText(staffCode),
          password: encryptText(passwordInput),
        }),
      });

      const text = await response.text();
      if (!response.ok) throw new Error("Invalid credentials");

      const refreshed = decryptText(text);
      const refreshedParsed = JSON.parse(refreshed);
      const token =
        refreshedParsed?.Token ||
        refreshedParsed?.token ||
        refreshedParsed?.accessToken ||
        refreshedParsed?.access_token;

      if (!token) throw new Error("Token not found in refreshed session");

      const decoded = jwtDecode(token);
      const now = Math.floor(Date.now() / 1000);
      const remaining = decoded?.exp ? Math.max(decoded.exp - now, 0) : 0;

      localStorage.setItem("adminLogsessiondetails", text);
      setCountdown(remaining);
      setShowSessionDialog(false);
      setPasswordInput("");
      setIsRefreshing(false);
      setSnackbar({ open: true, message: "Session extended!", severity: "success" });
    } catch (err) {
      setSessionError("Password incorrect or session refresh failed.");
      setIsRefreshing(false);
      setSnackbar({ open: true, message: "Password incorrect or session refresh failed.", severity: "error" });
    }
  };

  const handleChapterReport = () => {
    handleReportMenuClose();
    if (onOpenChapterReport) {
      onOpenChapterReport();
    }
  };

  const handleEventReport = () => {
    handleReportMenuClose();
    if (onOpenEventReport) {
      onOpenEventReport();
    }
  };

  return (
    <>
      <AppBar
        position="static"
        color="inherit"
        elevation={2}
        sx={{
          borderRadius: 2,
          mb: { xs: 2, md: 3 },
          backgroundColor: "#fff",
          color: "text.primary",
          boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
        }}
      >
        <Toolbar
          sx={{
            display: "flex",
            justifyContent: "space-between",
            minHeight: { xs: isMobileNav ? "auto" : 64, md: 64 },
            px: { xs: 1.5, sm: 3, md: 4 },
            py: { xs: isMobileNav ? 1.25 : 0, md: 0 },
            flexDirection: isMobileNav ? "column" : "row",
            alignItems: isMobileNav ? "stretch" : "center",
            gap: isMobileNav ? 1 : 0,
          }}
        >
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            gap={1.5}
            width="100%"
            minWidth={0}
          >
            <Box display="flex" alignItems="center" gap={isMobileNav ? 1.25 : 2} minWidth={0} flex={1}>
              <Avatar
                sx={{
                  width: isMobileNav ? 36 : 40,
                  height: isMobileNav ? 36 : 40,
                  flexShrink: 0,
                  bgcolor: "primary.main",
                  boxShadow: 2,
                }}
              >
                <AdminPanelSettingsIcon sx={{ color: "#fff", fontSize: isMobileNav ? 20 : 24 }} />
              </Avatar>
              <Box minWidth={0}>
                <Typography
                  variant="h6"
                  component="div"
                  noWrap
                  sx={{
                    fontWeight: 600,
                    color: "primary.main",
                    fontSize: { xs: "0.9rem", sm: "1.1rem", md: "1.25rem" },
                    lineHeight: 1.2,
                  }}
                >
                  {adminName ? `Welcome, ${adminName}` : "Admin Panel"}
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    color: "text.secondary",
                    fontSize: "0.7rem",
                    display: isMobileNav ? "block" : { xs: "none", sm: "block" },
                  }}
                >
                  Admin Dashboard
                </Typography>
              </Box>
            </Box>

            {isMobileNav ? (
              <IconButton
                color="inherit"
                edge="end"
                aria-label="open admin menu"
                onClick={(e) => setMobileMenuAnchor(e.currentTarget)}
                sx={{ flexShrink: 0, border: "1px solid", borderColor: "grey.300" }}
              >
                <MenuIcon />
              </IconButton>
            ) : (
              <Box display="flex" alignItems="center" gap={1} flexShrink={0}>
                {countdown !== null && (
                  <Chip
                    size="small"
                    icon={<AccessTimeIcon />}
                    label={`Session: ${countdown > 0 ? timeLeft : "Expired"}`}
                    sx={{
                      display: { xs: "none", sm: "inline-flex" },
                      height: 24,
                      fontSize: "0.72rem",
                      color:
                        countdown > 300
                          ? "#2e7d32"
                          : countdown > 60
                            ? "#ed6c02"
                            : "#d32f2f",
                      border: "1px solid",
                      borderColor:
                        countdown > 300
                          ? "#2e7d32"
                          : countdown > 60
                            ? "#ed6c02"
                            : "#d32f2f",
                      backgroundColor: "#fff",
                      "& .MuiChip-icon": {
                        color:
                          countdown > 300
                            ? "#2e7d32"
                            : countdown > 60
                              ? "#ed6c02"
                              : "#d32f2f",
                      },
                    }}
                  />
                )}
                {onOpenAdminMenu && (
                  <Tooltip title="Admin Menu">
                    <IconButton
                      color="default"
                      onClick={onOpenAdminMenu}
                      sx={{
                        border: "1px solid",
                        borderColor: "grey.300",
                        "&:hover": {
                          backgroundColor: "grey.100",
                          transform: "scale(1.05)",
                        },
                        transition: "all 0.3s ease",
                      }}
                    >
                      <AppsIcon />
                    </IconButton>
                  </Tooltip>
                )}
                <ProtectedComponent screenCode="ADMIN_REPORTS" permission="access">
                  <Tooltip title="Reports">
                    <IconButton
                      color="secondary"
                      onClick={handleReportMenuOpen}
                      sx={{
                        "&:hover": {
                          backgroundColor: "secondary.lighter",
                          transform: "scale(1.05)",
                        },
                        transition: "all 0.3s ease",
                      }}
                    >
                      <AssessmentIcon />
                    </IconButton>
                  </Tooltip>
                  <Menu
                    anchorEl={reportMenuAnchor}
                    open={Boolean(reportMenuAnchor)}
                    onClose={handleReportMenuClose}
                    anchorOrigin={{
                      vertical: "bottom",
                      horizontal: "right",
                    }}
                    transformOrigin={{
                      vertical: "top",
                      horizontal: "right",
                    }}
                  >
                    <MenuItem
                      onClick={() => {
                        handleReportMenuClose();
                        if (onOpenChapterReport) onOpenChapterReport(true);
                      }}
                    >
                      <ListItemIcon>
                        <GroupWorkIcon fontSize="small" />
                      </ListItemIcon>
                      <ListItemText primaryTypographyProps={{ fontSize: "0.75rem" }}>
                        All Chapters Members
                      </ListItemText>
                    </MenuItem>
                    <MenuItem
                      onClick={() => {
                        handleReportMenuClose();
                        if (onOpenEventReport) onOpenEventReport(true);
                      }}
                    >
                      <ListItemIcon>
                        <EventIcon fontSize="small" />
                      </ListItemIcon>
                      <ListItemText primaryTypographyProps={{ fontSize: "0.75rem" }}>
                        All Events Members
                      </ListItemText>
                    </MenuItem>
                  </Menu>
                </ProtectedComponent>
                {onOpenChat && (
                  <ProtectedComponent screenCode="ADMIN_GLOBAL_CHAT" permission="access">
                    <Tooltip title="Global Chat">
                      <IconButton
                        color="primary"
                        onClick={onOpenChat}
                        sx={{
                          "&:hover": {
                            backgroundColor: "primary.lighter",
                            transform: "scale(1.05)",
                          },
                          transition: "all 0.3s ease",
                        }}
                      >
                        <MessageIcon />
                      </IconButton>
                    </Tooltip>
                  </ProtectedComponent>
                )}
                {onOpenGroupChat && (
                  <Tooltip title="Group Chat">
                    <IconButton
                      color="info"
                      onClick={onOpenGroupChat}
                      sx={{
                        "&:hover": {
                          backgroundColor: "info.lighter",
                          transform: "scale(1.05)",
                        },
                        transition: "all 0.3s ease",
                      }}
                    >
                      <ForumIcon />
                    </IconButton>
                  </Tooltip>
                )}
                {isERPAdmin && onOpenScreenRights && (
                  <Tooltip title="Manage Screen Rights">
                    <IconButton
                      color="info"
                      onClick={onOpenScreenRights}
                      sx={{
                        "&:hover": {
                          backgroundColor: "info.lighter",
                          transform: "scale(1.05)",
                        },
                        transition: "all 0.3s ease",
                      }}
                    >
                      <SecurityIcon />
                    </IconButton>
                  </Tooltip>
                )}
                <Tooltip title="Logout">
                  <IconButton
                    color="error"
                    onClick={() => setLogoutDialogOpen(true)}
                    sx={{
                      "&:hover": {
                        backgroundColor: "error.lighter",
                        transform: "scale(1.05)",
                      },
                      transition: "all 0.3s ease",
                    }}
                  >
                    <LogoutIcon />
                  </IconButton>
                </Tooltip>
              </Box>
            )}
          </Box>

          {isMobileNav && countdown !== null && (
            <Box display="flex" justifyContent="center" width="100%">
              <Chip
                size="small"
                icon={<AccessTimeIcon />}
                label={countdown > 0 ? `Session: ${timeLeft}` : "Session expired"}
                sx={{
                  maxWidth: "100%",
                  height: 26,
                  fontSize: "0.7rem",
                  color:
                    countdown > 300
                      ? "#2e7d32"
                      : countdown > 60
                        ? "#ed6c02"
                        : "#d32f2f",
                  border: "1px solid",
                  borderColor:
                    countdown > 300
                      ? "#2e7d32"
                      : countdown > 60
                        ? "#ed6c02"
                        : "#d32f2f",
                  backgroundColor: "#fff",
                  "& .MuiChip-icon": {
                    color:
                      countdown > 300
                        ? "#2e7d32"
                        : countdown > 60
                          ? "#ed6c02"
                          : "#d32f2f",
                  },
                }}
              />
            </Box>
          )}
        </Toolbar>

        {isMobileNav && (
        <Menu
          anchorEl={mobileMenuAnchor}
          open={Boolean(mobileMenuAnchor)}
          onClose={closeMobileMenu}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
          transformOrigin={{ vertical: "top", horizontal: "right" }}
          PaperProps={{ sx: { minWidth: 220 } }}
        >
          {onOpenAdminMenu && (
            <MenuItem
              onClick={() => {
                closeMobileMenu();
                onOpenAdminMenu();
              }}
            >
              <ListItemIcon>
                <AppsIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText primary="Admin menu" />
            </MenuItem>
          )}
          <ProtectedComponent screenCode="ADMIN_REPORTS" permission="access">
            <MenuItem
              onClick={() => {
                closeMobileMenu();
                if (onOpenChapterReport) onOpenChapterReport(true);
              }}
            >
              <ListItemIcon>
                <GroupWorkIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText primary="All chapters members" />
            </MenuItem>
            <MenuItem
              onClick={() => {
                closeMobileMenu();
                if (onOpenEventReport) onOpenEventReport(true);
              }}
            >
              <ListItemIcon>
                <EventIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText primary="All events members" />
            </MenuItem>
          </ProtectedComponent>
          {onOpenChat && (
            <ProtectedComponent screenCode="ADMIN_GLOBAL_CHAT" permission="access">
              <MenuItem
                onClick={() => {
                  closeMobileMenu();
                  onOpenChat();
                }}
              >
                <ListItemIcon>
                  <MessageIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText primary="Global chat" />
              </MenuItem>
            </ProtectedComponent>
          )}
          {onOpenGroupChat && (
            <MenuItem
              onClick={() => {
                closeMobileMenu();
                onOpenGroupChat();
              }}
            >
              <ListItemIcon>
                <ForumIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText primary="Group chat" />
            </MenuItem>
          )}
          {isERPAdmin && onOpenScreenRights && (
            <MenuItem
              onClick={() => {
                closeMobileMenu();
                onOpenScreenRights();
              }}
            >
              <ListItemIcon>
                <SecurityIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText primary="Screen rights" />
            </MenuItem>
          )}
          <MenuItem
            onClick={() => {
              closeMobileMenu();
              setLogoutDialogOpen(true);
            }}
          >
            <ListItemIcon>
              <LogoutIcon fontSize="small" color="error" />
            </ListItemIcon>
            <ListItemText primary="Logout" primaryTypographyProps={{ color: "error.main" }} />
          </MenuItem>
        </Menu>
        )}
      </AppBar>

      <Dialog
        open={logoutDialogOpen}
        onClose={() => setLogoutDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            boxShadow: "0 8px 32px rgba(0,0,0,0.12)",
          },
        }}
      >
        <DialogTitle 
          sx={{ 
            pb: 1, 
            pt: 3,
            px: 3,
            fontWeight: 600, 
            color: "error.main",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Box display="flex" alignItems="center" gap={1}>
            <LogoutIcon sx={{ fontSize: 24 }} />
            Logout Confirmation
          </Box>
          <IconButton
            onClick={() => setLogoutDialogOpen(false)}
            size="small"
            sx={{
              color: "text.secondary",
              "&:hover": {
                backgroundColor: "action.hover",
                color: "text.primary",
              },
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ px: 3, py: 2 }}>
          <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.6 }}>
            Are you sure you want to logout from the Admin Panel?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3, pt: 2, gap: 1.5 }}>
          <Button
            onClick={() => setLogoutDialogOpen(false)}
            color="primary"
            variant="outlined"
            startIcon={<CloseIcon />}
            sx={{ 
              textTransform: "none",
              minWidth: 100,
              borderColor: "primary.main",
              color: "primary.main",
              "&:hover": {
                borderColor: "primary.dark",
                backgroundColor: "primary.lighter",
                color: "primary.dark",
              },
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleLogout}
            color="error"
            variant="contained"
            startIcon={<LogoutIcon />}
            sx={{ 
              textTransform: "none",
              minWidth: 100,
              "&:hover": {
                backgroundColor: "error.dark",
              },
            }}
          >
            Logout
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={showSessionDialog}
        onClose={() => {}}
        disableEscapeKeyDown
      >
        <DialogTitle>Session Expiring</DialogTitle>
        <form onSubmit={handleSessionRefresh}>
          <DialogContent>
            <Typography gutterBottom>
              Your session is ending soon. Please enter your password to continue this session.
            </Typography>
            <FormControlLabel
              control={
                <Checkbox
                  checked={logoutChecked}
                  onChange={(e) => {
                    if (e.target.checked) {
                      setShowLogoutConfirm(true);
                    } else {
                      setLogoutChecked(false);
                    }
                  }}
                  color="secondary"
                />
              }
              label="Logout and go to login page"
              sx={{ mb: 1 }}
            />
            <TextField
              label="Password"
              type="password"
              fullWidth
              value={passwordInput}
              onChange={(e) => setPasswordInput(e.target.value)}
              disabled={isRefreshing || logoutChecked}
              autoFocus
              margin="dense"
              onKeyDown={(e) => {
                if (e.key === "Enter" && !logoutChecked) {
                  handleSessionRefresh(e);
                }
              }}
            />
            {sessionError && (
              <Typography color="error" variant="body2" sx={{ mt: 1 }}>
                {sessionError}
              </Typography>
            )}
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => {
                setShowSessionDialog(false);
                handleLogout();
              }}
            
              disabled={isRefreshing || !logoutChecked}
              startIcon={<LogoutIcon />}
            >
              Logout
            </Button>
            <Button
              type="submit"
              color="info"
              disabled={isRefreshing || !passwordInput || logoutChecked}
              variant="contained"
            >
              {isRefreshing ? "Refreshing..." : "Continue Session"}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      <Dialog
        open={showLogoutConfirm}
        onClose={() => setShowLogoutConfirm(false)}
      >
        <DialogTitle>Confirm Logout</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to logout? It&apos;s best to enter your password to continue the same session.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setShowLogoutConfirm(false);
              setLogoutChecked(false);
            }}
            color="primary"
          >
            Cancel
          </Button>
          <Button
            onClick={() => {
              setShowLogoutConfirm(false);
              setLogoutChecked(true);
              handleLogout();
            }}
            color="secondary"
            autoFocus
          >
            Logout
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

// Typechecking props for AdminNavBar
AdminNavBar.propTypes = {
  onOpenChat: PropTypes.func,
  onOpenGroupChat: PropTypes.func,
  onOpenChapterReport: PropTypes.func, // Can accept boolean for "all" view
  onOpenEventReport: PropTypes.func, // Can accept boolean for "all" view
  onOpenScreenRights: PropTypes.func,
  onOpenAdminMenu: PropTypes.func,
};

export default AdminNavBar; 